import { Box, Checkbox, FormControlLabel, Grow, List, ListItem, Radio, RadioGroup, Typography, useMediaQuery } from "@mui/material"
import { Icon } from "components"
import { ColumnTask, DraggableChoice, DraggableColumn } from "components/columnTask"
import { TextField } from "components/textField"
import { theme } from "config"
import { observer } from "mobx-react"
import { Choice, Task } from "models"
import React from "react"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import { TaskType } from "types";
import { Loading } from "views/loading"


interface IQuizTaskProps {
  task: Task
  setAreColumnAnswersChecked: (checked: boolean) => void
}

export const QuizTask = observer((props: IQuizTaskProps) => {
  const { task, setAreColumnAnswersChecked } = props
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  if (!task) return null

  let inputs
  switch (task.type) {
    case TaskType.Input:
      inputs = (
        <ListItem key={'input'} className="quizTask-INPUT">
          <NumericalTaskInput
            fullWidth
            sx={{ borderRadius: '6px' }}
            type='number'
            isMobile={isMobile}
            $correct={task.showAnswer ? task.isAnswerCorrect : undefined}
            value={task.input || ''}
            onChange={(e) => task.set('input', e.target.value, false)}
          />
        </ListItem>
      )
      break
    case TaskType.Single:
      inputs = (
        <RadioGroup
          onChange={(e, value: string) =>
            task.toggleSelect(task.choices.find((c) => c.id === Number(value)))
          }
        >
          {task.choices.map((c) => (
            <ListItem key={c.id} className='quizTask-Single'>
              <div
                style={{ position: 'relative', width: '100%' }}
              >
                <StyledFormControlLabel
                  sx={{ width: '100%' }}
                  checked={Boolean(c.is_selected)}
                  value={c.id}
                  $correct={task.showAnswer ? c.isCorrect : undefined}
                  label={c.text}
                  control={
                    <HiddenRadio
                      sx={{
                        '&:hover': { bgcolor: 'transparent' },
                      }}
                    />
                  }
                />
                {task.showAnswer && c.is_selected && c.isCorrect && (
                  <CheckCircle>
                    <Icon size='size-12' color='white' iconId='checkmark' />
                  </CheckCircle>
                )}
              </div>
            </ListItem>
          ))}
        </RadioGroup>
      )
      break
    case TaskType.Column:
      inputs = (
        <ListItem key={'column'} className='quizTask-COLUMN-LISTITEM'>
          <div
            style={{ position: 'relative', width: '100%' }}
          >
            <ColumnTask
              task={task}
              onAnswer={(choice: Choice, columnId: number | string) => {
                task.set('input', '')
              }}
              onCorrect={(answers) => {
                console.log('answers', answers)
                task.set('input', 'finished')
              }}
              onIncorrect={(answers) => {
                console.log('incorrect', answers)
                task.set('input', 'finished')
              }}
              onAllAnswered={(total, correct, wrong) => {
                console.log('all answered', total, correct, wrong)
                task.set('input', 'finished')
              }}
              minCorrect={task.choices?.length - 1}
              maxCorrect={task.choices?.length}
              setAreColumnAnswersChecked={setAreColumnAnswersChecked}
            />
          </div>
        </ListItem>
      )
      break
    case TaskType.Multi:
      inputs = task.choices.map((c) => (
        <ListItem
          key={c.id}
          sx={{ position: 'relative' }}
          className='quizTask-MULTI'
        >
          <div style={{ position: 'relative', width: '100%' }}>
            <StyledFormControlLabel
              sx={{ width: '100%' }}
              checked={Boolean(c.is_selected)}
              onChange={() => task.toggleSelect(c)}
              value={c.id}
              control={<Checkbox />}
              label={c.text}
              $isMulti={true}
              $correct={task.showAnswer ? c.isCorrect : undefined}
            />
            {task.showAnswer && c.isCorrect && Boolean(c.is_selected) && (
              <CheckCircle>
                <Icon size='size-12' color='white' iconId='checkmark' />
              </CheckCircle>
            )}
          </div>
        </ListItem>
      ))
      break
  }
  return (
    <StyledList sx={{ mt: 2 }} className='quizTask-STYLEDLIST'>
      <Typography
        textAlign='center'
        marginBottom={3}
        variant={isMobile ? 'h6' : 'h5'}
      >
        {task.question}
      </Typography>
      {inputs}
      <Grow
        key={task.id}
        in={
          task.showAnswer &&
          Boolean(
            task.isAnswerCorrect
              ? task.correct_feedback
              : task.incorrect_feedback
          )
        }
        mountOnEnter
      >
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 2,
              py: 1,
              backgroundColor: task.isAnswerCorrect ? '#e0fbe0' : '#fdecea',
              border: `1px solid ${
                task.isAnswerCorrect ? '#2ecc71' : '#e74c3c'
              }`,
              borderRadius: '6px',
            }}
          > 
            <Typography variant='body1' fontWeight={500}>
              {task.isAnswerCorrect
                ? task.correct_feedback
                : task.incorrect_feedback} 
            </Typography>
          </Box>
        </Box>
      </Grow>
    </StyledList>
  )
})

const NumericalTaskInput = styled(TextField)<{
  isMobile: boolean
  $correct?: boolean
}>`
  margin-top: 4rem;
  .MuiOutlinedInput-root {
    border-radius: 6px;
  }
  width: ${(p) => (p.isMobile ? '80%' : '100%')};
  border: ${(props) => {
    if (props.$correct === undefined) return undefined
    return props.$correct ? '2px solid #00AB84' : '2px solid #D6383D'
  }};
`

const StyledList = styled(List)`
  .MuiListItem-root {
    padding-left: 0;
    padding-right: 0;
    justify-content: center;
  }
`

const HiddenRadio = styled(Radio)`
  span {
    display: none;
  }
`

const StyledFormControlLabel = styled(FormControlLabel)<{
  checked: boolean
  $correct?: boolean
  $isMulti?: boolean
}>`
  background: white;
  padding: 1.2rem 1.2rem;
  margin-right: 0;
  border-radius: 6px;
  border: ${(props) => {
    if (props.$correct === true) return '2px solid #00AB84'
    if (!props.checked) return undefined
    if (props.$correct === undefined) return '2px solid #275A86'
    return props.$correct ? '2px solid #00AB84' : '2px solid #D6383D'
  }};
`

const CheckCircle = styled(Box)`
  border-radius: 50%;
  margin-right: 1rem;
  display: flex;
  justify-content: center;
  padding: 0.8rem 0.8rem;
  border: 1px solid;
  border-color: ${theme.palette.lightGreen.main};
  background-color: ${theme.palette.lightGreen.main};
  position: absolute;
  right: 0.5rem;
  top: calc(50% - 15px);

  svg {
    stroke-width: 3;
  }
`
